import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/sclogo-250-1.svg';
import TenantTable from './TenantTable';
import AddTenantModal from './AddTenantModal';
import DataConnection from './DataConnection';
import NotificationSettings from './NotificationSettings';

/**
 * Admin Panel Component
 * 
 * Displays tenant (vendor) management for admin users
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */
const AdminPanel = () => {
    const [user, setUser] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('tenants');
    const [showAddTenantModal, setShowAddTenantModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, tenant: null });
    const navigate = useNavigate();

    useEffect(() => {
        const init = async () => {
            const isAuthed = verifyAdminAccess();
            if (isAuthed) {
                loadTenants();
            }
        };
        init();
    }, [navigate]);

    const verifyAdminAccess = () => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
            navigate('/');
            return false;
        }

        // Simulate user data (in real app, verify from token)
        setUser({
            name: 'Pedro Ventura',
            email: 'pedro@acqualytics.com',
            tenant: {
                id: 2,
                domain: 'acqualytics.com',
                name: 'Acqualytics',
                isAdmin: true
            }
        });
        return true;
    };

    const loadTenants = async () => {
        try {
            setIsLoading(true);
            
            // Fetch all tenants from the database
            const response = await fetch('/api/admin/tenants', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success) {
                // Transform the data to include user counts for each tenant
                const tenantsWithUserCounts = await Promise.all(
                    data.tenants.map(async (tenant) => {
                        try {
                            const usersResponse = await fetch(`/api/tenant/${tenant.id}/users`, {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                                }
                            });
                            
                            const usersData = await usersResponse.json();
                            const userCount = usersData.success ? usersData.users.length : 0;
                            
                            return {
                                ...tenant,
                                userCount,
                                createdAt: new Date(tenant.created_at).toLocaleDateString(),
                                lastActivity: tenant.updated_at ? new Date(tenant.updated_at).toLocaleDateString() : 'Never'
                            };
                        } catch (error) {
                            console.error(`Failed to load users for tenant ${tenant.id}:`, error);
                            return {
                                ...tenant,
                                userCount: 0,
                                createdAt: new Date(tenant.created_at).toLocaleDateString(),
                                lastActivity: 'Unknown'
                            };
                        }
                    })
                );
                
                setTenants(tenantsWithUserCounts);
            } else {
                throw new Error(data.message || 'Failed to load tenants');
            }
        } catch (error) {
            console.error('Failed to load tenants:', error);
            // Show error message to user
            alert('Failed to load tenant data. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        navigate('/');
    };

    const handleDeleteTenant = (tenant) => {
        if (tenant.isAdmin) {
            return; // Admin tenants can't be deleted, button shouldn't show anyway
        }
        setDeleteConfirm({ show: true, tenant });
    };
    
    const confirmDeleteTenant = async () => {
        const tenant = deleteConfirm.tenant;
        if (!tenant) return;
        
        try {
            const response = await fetch(`/api/admin/tenants/${tenant.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            
            const data = await response.json();
            
            if (data.success) {
                // Remove from local state
                setTenants(tenants.filter(t => t.id !== tenant.id));
            } else {
                console.error('Delete failed:', data.message);
            }
        } catch (error) {
            console.error('Failed to delete tenant:', error);
        }
        
        setDeleteConfirm({ show: false, tenant: null });
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            active: 'bg-green-100 text-green-800',
            inactive: 'bg-gray-100 text-gray-800',
            suspended: 'bg-red-100 text-red-800'
        };
        
        return (
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[status] || statusStyles.inactive}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4" />
                    <p className="text-lg font-semibold text-gray-700">Loading Admin Panel</p>
                    <p className="text-gray-500">Fetching tenant data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <img src={logo} alt="SureCost" className="h-8 w-auto" />
                        <div className="h-6 w-px bg-gray-300" />
                        <h1 className="text-sm font-semibold text-gray-900">Admin Panel</h1>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={handleBackToDashboard}
                            style={{ 
                                all: 'initial',
                                fontFamily: 'inherit',
                                background: 'transparent',
                                border: '0',
                                padding: '0',
                                margin: '0',
                                cursor: 'pointer',
                                outline: '0',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                color: '#2563eb',
                                textDecoration: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                boxSizing: 'border-box',
                                display: 'inline-block',
                                verticalAlign: 'baseline'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
                            onMouseLeave={(e) => e.target.style.color = '#2563eb'}
                        >
                            Dashboard
                        </button>
                        
                        <button 
                            onClick={handleLogout}
                            style={{ 
                                all: 'initial',
                                fontFamily: 'inherit',
                                background: 'transparent',
                                border: '0',
                                padding: '0',
                                margin: '0',
                                cursor: 'pointer',
                                outline: '0',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                color: '#4b5563',
                                textDecoration: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                boxSizing: 'border-box',
                                display: 'inline-block',
                                verticalAlign: 'baseline'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#1f2937'}
                            onMouseLeave={(e) => e.target.style.color = '#4b5563'}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <div className="p-4">
                {/* Navigation Tabs */}
                <div className="mb-6">
                    <nav className="flex space-x-1">
                        <button
                            onClick={() => setActiveTab('tenants')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors border-0 outline-none focus:outline-none focus:ring-0 ${
                                activeTab === 'tenants'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                            style={{ 
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none'
                            }}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            Tenant Management
                        </button>
                        <button
                            onClick={() => setActiveTab('data-connection')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors border-0 outline-none focus:outline-none focus:ring-0 ${
                                activeTab === 'data-connection'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                            style={{ 
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none'
                            }}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                            </svg>
                            Data Connection
                        </button>
                        <button
                            onClick={() => setActiveTab('notifications')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors border-0 outline-none focus:outline-none focus:ring-0 ${
                                activeTab === 'notifications'
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                            style={{ 
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none'
                            }}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Notifications
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                {activeTab === 'tenants' && (
                    <>
                        {/* Page Header */}
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900">Tenant Management</h1>
                                <p className="text-xs text-gray-500">Manage vendor tenants and their access to the SureCost platform</p>
                            </div>
                            <button
                                onClick={() => setShowAddTenantModal(true)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.375rem 0.75rem',
                                    backgroundColor: '#2563eb',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: '500',
                                    borderRadius: '0.5rem',
                                    border: 'none',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                                    fontFamily: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#1d4ed8';
                                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#2563eb';
                                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                                }}
                            >
                                <svg style={{ width: '0.75rem', height: '0.75rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Tenant
                            </button>
                        </div>

                {/* Stats Row */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-gray-900">{tenants.length}</div>
                                <div className="text-xs font-medium text-gray-500">Total Tenants</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-50 to-green-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-gray-900">{tenants.filter(t => t.status === 'active').length}</div>
                                <div className="text-xs font-medium text-gray-500">Active Tenants</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-gray-900">{tenants.filter(t => t.isAdmin).length}</div>
                                <div className="text-xs font-medium text-gray-500">Admin Tenants</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg flex items-center justify-center">
                                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-xl font-bold text-gray-900">{tenants.reduce((sum, t) => sum + t.userCount, 0)}</div>
                                <div className="text-xs font-medium text-gray-500">Total Users</div>
                            </div>
                        </div>
                    </div>
                </div>

                        {/* Tenants Section */}
                        <div className="mt-4">
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">All Tenants</h2>
                            
                            <TenantTable 
                                tenants={tenants}
                                onView={() => {}}
                                onEdit={() => {}}
                                onDelete={handleDeleteTenant}
                            />
                            
                            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                                <div>Showing {tenants.length} tenants</div>
                                <div>Last updated: {new Date().toLocaleTimeString()}</div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'data-connection' && (
                    <DataConnection />
                )}

                {activeTab === 'notifications' && (
                    <NotificationSettings />
                )}
            </div>

            {/* Add Tenant Modal */}
            <AddTenantModal
                isOpen={showAddTenantModal}
                onClose={() => setShowAddTenantModal(false)}
                onTenantAdded={(newTenant) => {
                    // Add the new tenant to the list
                    setTenants(prev => [newTenant, ...prev]);
                    console.log(`✅ Added new tenant: ${newTenant.name}`);
                }}
            />
            
            {/* Delete Confirmation Modal */}
            {deleteConfirm.show && deleteConfirm.tenant && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '400px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: '#fef2f2',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="24" height="24" fill="none" stroke="#dc2626" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>Delete Tenant</h3>
                                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>This action cannot be undone</p>
                            </div>
                        </div>
                        
                        <div style={{
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '20px'
                        }}>
                            <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                                Are you sure you want to delete <strong>{deleteConfirm.tenant.name}</strong> ({deleteConfirm.tenant.domain})?
                            </p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                                This will also delete all {deleteConfirm.tenant.userCount || 0} associated users.
                            </p>
                        </div>
                        
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setDeleteConfirm({ show: false, tenant: null })}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#374151',
                                    backgroundColor: '#f3f4f6',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteTenant}
                                style={{
                                    padding: '10px 20px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'white',
                                    backgroundColor: '#dc2626',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer'
                                }}
                            >
                                Delete Tenant
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
